import React, { useState, useEffect, useCallback, useRef } from 'react';
import i18next from 'i18next';
import clsx from 'clsx';

import { useParams, useHistory } from 'react-router-dom';
import axiosInstance from '../../config/axiosConfig';
import dayjs from 'dayjs';

import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';

import renderQuestions from './renderQuestions';

import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';

const t = (i18nKey, params) => i18next.t(`userDashboard.candidate.${i18nKey}`, params);

const CandidateDetails = () => {
    const [loading, setLoading] = useState(true);

    const [candidate, setCandidate] = useState(null);
    const [campaign, setCampaign] = useState(null);

    const [testAttempts, setTestAttempts] = useState([]);
    const [testsDetails, setTestsDetails] = useState([]);

    const [unlockDialogVisible, setUnlockDialogVisible] = useState(false);
    const [loadingUnlock, setLoadingUnlock] = useState(false);
    const [testToUnlock, setTestToUnlock] = useState(null);

    const [tab, setTab] = useState('details');

    const toast = useRef(null);

    const history = useHistory();
    const { candidate_id } = useParams();

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const [selectedAttempts, setSelectedAttempts] = useState([]);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [reEngageLoading, setReEngageLoading] = useState(false);
    const [reEngageSent, setReEngageSent] = useState(false);
    const [showPdfDialog, setShowPdfDialog] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    const fetchCandidateDetails = useCallback(async () => {
        setLoading(true);

        try {
            const response = await axiosInstance.get(`/dashboard/candidates/${selectedCompany?.company_id}/${candidate_id}/getDetails`);
            setCandidate(response.data.candidate);
            setCampaign(response.data.campaign);
            setTestAttempts(response.data.test_attempts);
            setTestsDetails(response.data.tests_details);

        } catch (error) {
            if (error.response && error.response.status === 404) {
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Candidat non trouvé', life: 3000 });
                window.location.href = '/dashboard/campaigns/';
            } else {
                console.error(error);
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la récupération des détails du candidat.', life: 3000 });
            }
        } finally {
            setLoading(false);
        }
    }, [candidate_id, selectedCompany?.company_id]);

    useEffect(() => {
        fetchCandidateDetails();
    }, [fetchCandidateDetails]);

    const testStatusTemplate = (rowData) => {
        let statusClass = '';
        const testCompleted = candidate.choosen_tests.some(test => Object.keys(test)[0] === rowData.ref);
        const status = testCompleted ? 'complete' : 'incomplete';

        switch (status) {
            case 'complete':
                statusClass = 'd-success-tag';
                break;
            case 'pending':
                statusClass = 'd-pause-tag';
                break;
            case 'incomplete':
                statusClass = 'd-inactive-tag';
                break;
            default:
                statusClass = 'd-light-tag';
        }

        return <span className={statusClass + ' no-wrap'}>{i18next.t(`userDashboard.campaigns.participants_table_tag.${testCompleted ? '1' : '0'}`)}</span>;
    };

    const testScoreTemplate = (rowData) => {
        let attempt = null;
        candidate.choosen_tests.forEach(test => {
            if (test.hasOwnProperty(rowData.ref)) {
                attempt = testAttempts.find(attempt => attempt.ref === test[rowData.ref]);
            }
        });
        return attempt ? `${attempt.score}%` : '-';
    };

    const testDateTemplate = (rowData) => {
        let attempt = null;
        candidate.choosen_tests.forEach(test => {
            if (test.hasOwnProperty(rowData.ref)) {
                attempt = testAttempts.find(attempt => attempt.ref === test[rowData.ref]);
            }
        });
        return attempt ? dayjs(attempt.end_at).format('DD/MM/YYYY HH:mm') : '-';
    };

    const getCompletionPercentage = () => {
        if (!candidate || !campaign) return 0;
        const totalTests = campaign.choosen_tests.length;
        const completedTests = candidate.choosen_tests.length;
        return Math.round((completedTests / totalTests) * 100);
    };

    const isFollowUpCandidate = () => {
        if (!candidate || !campaign) return false;

        if (campaign.choosen_tests?.length === candidate?.choosen_tests?.length) {
            return false;
        };

        return true;
    };

    const getLastActivityDate = () => {
        if (!testAttempts.length) return null;
        const lastAttempt = testAttempts.reduce((latest, attempt) => {
            return dayjs(attempt.end_at).isAfter(dayjs(latest.end_at)) ? attempt : latest;
        });
        return dayjs(lastAttempt.end_at).format('DD/MM/YY - HH:mm');
    };


    const handleUnlockTest = async () => {
        if (!testToUnlock || !selectedCompany?.company_id) return;

        setLoadingUnlock(true);
        try {
            const response = await axiosInstance.post(`/dashboard/candidates/${selectedCompany.company_id}/${candidate_id}/unlockTest`, testToUnlock);
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Test débloqué avec succès', life: 3000 });
            setUnlockDialogVisible(false);
            fetchCandidateDetails();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec du déblocage du test', life: 3000 });
        } finally {
            setLoadingUnlock(false);
        }
    };

    const handleCandidateFollowUpEmail = async () => {
        setReEngageLoading(true);
        try {

            await axiosInstance.post(`/dashboard/candidates/${selectedCompany.company_id}/${candidate_id}/followup-email`);

            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'E-mail envoyée avec succès' });
            setReEngageSent(true);

        } catch (error) {
            console.error(error);

            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec du de l\'envoie de l\'e-mail veuillez réessayer plus tard', life: 5000 });
            setReEngageSent(false);
        } finally {
            setReEngageLoading(false);
        }
    };

    const handleRowClick = (rowData) => {
        const testAttemptObject = candidate.choosen_tests.find(test => Object.keys(test)[0] === rowData.ref);

        const testRef = testAttemptObject ? testAttemptObject[rowData.ref] : null;
        const testAttempt = testAttempts.find(attempt => attempt.ref === testRef);

        const isUnlocked = candidate.test_access || (testAttemptObject && candidate.unlocked_tests?.includes(testRef));

        if (!testAttempt) return toast.current.show({ severity: 'warn', summary: t('errors.access_refused'), detail: t('errors.access_refused_content'), life: 3000 });

        if (isUnlocked) {
            if (testAttempt) {
                history.push(`/dashboard/campaigns/${campaign.campaign_id}/candidates/${candidate.candidate_id}/${testAttempt.ref}`);
            } else {
                history.push(`/dashboard/campaigns/${campaign.campaign_id}/candidates/${candidate.candidate_id}/startTest/${rowData.ref}`);
            }
        } else if (testAttemptObject && !isUnlocked) {
            setTestToUnlock({ test_ref: rowData.ref, attempt_ref: testAttempt ? testAttempt.ref : null });
            setUnlockDialogVisible(true);
        }
    };

    const handleDownloadReports = async () => {
        if (!selectedAttempts || selectedAttempts.length < 2) {
            toast.current.show({
                severity: 'warn',
                summary: t('errors.selection_required'),
                detail: t('errors.select_min_two_tests'),
                life: 3000
            });
            return;
        }

        setDownloadLoading(true);
        try {
            const attempt_ids = selectedAttempts.map(test => {
                const testObj = candidate.choosen_tests.find(t => Object.keys(t)[0] === test.ref);
                return testObj ? testObj[test.ref] : null;
            }).filter(id => id !== null);

            const response = await axiosInstance({
                url: `/dashboard/candidates/${selectedCompany?.company_id}/${candidate_id}/bulkGeneratePdf`,
                method: 'POST',
                responseType: 'blob',
                data: { attempt_ids }
            });

            if (response.headers['content-type'] !== 'application/pdf') {
                throw new Error('Invalid response type');
            }

            // Get filename from headers or create default
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Combined_Reports_${candidate_id}.pdf`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch && filenameMatch.length > 1) {
                    filename = filenameMatch[1];
                }
            }

            // Create and trigger download
            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const pdfUrl = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(pdfUrl);

            toast.current.show({
                severity: 'success',
                summary: 'Succès',
                detail: 'PDF téléchargé avec succès !'
            });

        } catch (error) {
            console.error('Download error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors de la création du PDF.',
                life: 3000
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleViewReports = async () => {
        if (!selectedAttempts || selectedAttempts.length < 2) {
            toast.current.show({
                severity: 'warn',
                summary: t('errors.selection_required'),
                detail: t('errors.select_min_two_tests'),
                life: 3000
            });
            return;
        }

        setDownloadLoading(true);
        try {
            const attempt_ids = selectedAttempts.map(test => {
                const testObj = candidate.choosen_tests.find(t => Object.keys(t)[0] === test.ref);
                return testObj ? testObj[test.ref] : null;
            }).filter(id => id !== null);

            const response = await axiosInstance({
                url: `/dashboard/candidates/${selectedCompany?.company_id}/${candidate_id}/bulkGeneratePdf`,
                method: 'POST',
                responseType: 'blob',
                data: { attempt_ids }
            });

            if (response.headers['content-type'] !== 'application/pdf') {
                throw new Error('Invalid response type');
            }

            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(pdfBlob);
            setPdfUrl(url);
            setShowPdfDialog(true);

        } catch (error) {
            console.error('View error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors de la création du PDF.',
                life: 3000
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    if (loading) {
        return (
            <section className="home">
                <div className="container">
                    <div className="columns is-vcentered is-multiline">
                        <div className="column">
                            <h1 className="title is-3 mb-0">
                                <Skeleton width="50%" height="2rem" />
                            </h1>
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column">
                            <div>
                                <span className="subtitle is-4 has-text-grey has-text-weight-bold mb-0">
                                    <Skeleton width="40%" height="1.5rem" className='mb-1' />
                                </span>
                                <span className="has-text-grey has-text-weight-bold mt-0 mb-6">
                                    <Skeleton width="10%" height="1.5rem" className='mb-1' />
                                </span>
                            </div>

                            <span>
                                <Skeleton width="27%" height="1rem" className='mb-1 mt-5' />
                            </span>

                            <div className="column columns">
                                <div className="column pl-0">
                                    <Skeleton width="40%" height="1rem" />
                                </div>

                                <div className="column has-text-right">
                                    <div className='is-flex is-justify-content-right'>
                                        <Skeleton width="40%" height="2.5rem" />
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (!campaign || !candidate) {
        return null;
    }

    const completionPercentage = getCompletionPercentage();
    const lastActivityDate = getLastActivityDate();
    const followUpCandidate = isFollowUpCandidate();

    return (
        <section className="home">
            <Toast ref={toast} position="bottom-right" />

            <Dialog header="Débloquer le test" dismissableMask={!loadingUnlock} visible={unlockDialogVisible} style={{ width: '50vw' }} onHide={() => setUnlockDialogVisible(false)} modal>

                <div className="has-text-centered">
                    <p className="title is-4">{`${candidate.first_name || ''} ${candidate.last_name || ''} ${candidate.username ? `- ${candidate.username}` : ''}`}</p>
                    <p className="subtitle is-5">{`Test : ${testsDetails.find(test => test.ref === testToUnlock?.test_ref)?.title || 'Titre du test'}`}</p>
                    <p style={{ textDecoration: 'line-through' }}>Voulez-vous débloquer ce test pour 1 crédit ?</p>
                    <p>Voulez-vous débloquer tous les tests actuels de ce candidat pour 1 crédit ?</p>
                </div>

                <div className="is-flex is-justify-content-center mt-4">
                    <Button label="Annuler" severity="secondary" className="mr-2" onClick={() => setUnlockDialogVisible(false)} />
                    <Button label="Oui" onClick={handleUnlockTest} loading={loadingUnlock} />
                </div>
            </Dialog>

            <Dialog
                header="PDF Report"
                visible={showPdfDialog}
                onHide={() => setShowPdfDialog(false)}
                style={{ width: '100vw', height: '100vh', maxWidth: '80vw', maxHeight: '100vh' }}
                modal
                dismissableMask
            >
                <iframe src={pdfUrl} width="100%" height="100%" title="Combined PDF Report" />
            </Dialog>

            <div className="container">
                <div className="columns is-vcentered is-multiline">
                    <div className="column">
                        <h1 className="title is-4 mb-0">
                            {`${candidate.first_name || ''} ${candidate.last_name || ''} ${candidate.username ? `- ${candidate.username}` : ''}`} <span className="subtitle is-6 has-text-grey-dark">({candidate?.email || 'Aucun e-mail enregistré'})</span>
                        </h1>
                    </div>
                </div>

                <div className="columns">
                    <div className="column">

                        <div className='mb-5'>
                            <span className="subtitle is-5 has-text-grey has-text-weight-bold mb-0">{campaign?.campaign_name} -  <span className="is-size-6 has-text-grey mt-0 mb-5">{selectedCompany?.name}</span></span>
                        </div>

                        <p className="mb-0">
                            <span className="icon has-text-success mr-1">
                                <i className="fa-solid fa-circle-check" style={{ color: 'grey' }}></i>
                            </span>
                            {t('last_activity')} : <strong>{lastActivityDate || t('no_activity')}</strong>
                        </p>

                        <div className="column columns">

                            <div className="column pl-0">
                                <p className="">
                                    <span className={clsx('icon mr-1', { 'has-text-success': completionPercentage === 100, 'has-text-danger': !completionPercentage, 'has-text-warning': completionPercentage >= 1 && completionPercentage !== 100 })}>
                                        <i className="fa-solid fa-circle-check"></i>
                                    </span>
                                    {completionPercentage}% {t('completition_percentage')}
                                    <button className={clsx('button is-small radius-10 is-light ml-2', { 'disabled': !followUpCandidate || reEngageLoading || reEngageSent })}
                                        onClick={handleCandidateFollowUpEmail}
                                        disabled={!followUpCandidate || reEngageLoading || reEngageSent}
                                    >
                                        {reEngageLoading && <i className="pi pi-spinner pi-spin mr-2"></i>}
                                        {t('re_engage')} "<strong style={{ maxWidth: 150, textOverflow: 'ellipsis', overflow: 'hidden' }}>{`${candidate.first_name ? `${candidate.first_name} ${candidate.last_name}` : t('participant')}`}</strong>" {t('through_email')}
                                    </button>
                                </p>
                            </div>

                            <div className="column has-text-right">
                                <button
                                    className="button is-outlined radius-8"
                                    onClick={handleDownloadReports}
                                    disabled={selectedAttempts.length < 2 || downloadLoading}
                                >
                                    <span className="icon">
                                        {downloadLoading ? (
                                            <ProgressSpinner style={{ width: '20px', height: '20px' }} />
                                        ) : (
                                            <i className="pi pi-download"></i>
                                        )}
                                    </span>
                                    <span>{t('download_reports')}</span>
                                </button>

                                <button
                                    className="button is-outlined ml-1 px-4 radius-8"
                                    onClick={handleViewReports}
                                    disabled={selectedAttempts.length < 2 || downloadLoading}
                                >
                                    <span className="icon">
                                        {downloadLoading ? (
                                            <ProgressSpinner style={{ width: '20px', height: '20px' }} />
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" style={{ fill: '#363636' }}>
                                                <path d="M288 80c-65.2 0-118.8 29.6-159.9 67.7C89.6 183.5 63 226 49.4 256c13.6 30 40.2 72.5 78.6 108.3C169.2 402.4 222.8 432 288 432s118.8-29.6 159.9-67.7C486.4 328.5 513 286 526.6 256c-13.6-30-40.2-72.5-78.6-108.3C406.8 109.6 353.2 80 288 80zM95.4 112.6C142.5 68.8 207.2 32 288 32s145.5 36.8 192.6 80.6c46.8 43.5 78.1 95.4 93 131.1c3.3 7.9 3.3 16.7 0 24.6c-14.9 35.7-46.2 87.7-93 131.1C433.5 443.2 368.8 480 288 480s-145.5-36.8-192.6-80.6C48.6 356 17.3 304 2.5 268.3c-3.3-7.9-3.3-16.7 0-24.6C17.3 208 48.6 156 95.4 112.6zM288 336c44.2 0 80-35.8 80-80s-35.8-80-80-80c-.7 0-1.3 0-2 0c1.3 5.1 2 10.5 2 16c0 35.3-28.7 64-64 64c-5.5 0-10.9-.7-16-2c0 .7 0 1.3 0 2c0 44.2 35.8 80 80 80zm0-208a128 128 0 1 1 0 256 128 128 0 1 1 0-256z" />
                                            </svg>
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="tabs is-boxed is-centered">
                    <ul>
                        <li className={clsx({ 'is-active': tab === 'details' })} onClick={() => setTab('details')}>
                            <a>
                                <span>Tests</span>
                            </a>
                        </li>
                        <li className={clsx({ 'is-active': tab === 'questionnaire' })} onClick={() => setTab('questionnaire')}>
                            <a>
                                <span>{t('questions')}</span>
                            </a>
                        </li>
                    </ul>
                </div>

                {tab === 'details' && (
                    <>
                        <div className="columns is-vcentered is-justify-content-center px-6">
                            <Message
                                className='mx-6'
                                severity="info"
                                text={t('limited_notification')}
                            />
                        </div>

                        <div className="columns is-vcentered is-multiline">

                            <div className="column">

                                <DataTable
                                    value={campaign.choosen_tests}
                                    onRowClick={(e) => handleRowClick(e.data)}
                                    selection={selectedAttempts}
                                    onSelectionChange={(e) => setSelectedAttempts(e.value)}
                                    selectionMode="multiple"
                                >
                                    <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />
                                    <Column field="" header="" body={(rowData) => {
                                        const testColor = testsDetails.find(test => test.ref === rowData.ref);
                                        return <i className='fa-solid fa-circle' style={{ color: testColor?.test_color || '#fff' }}></i>
                                    }} />

                                    <Column field="ref" header={t('candidate_details_table.0')} body={(rowData) => {
                                        const testDetail = testsDetails.find(test => test.ref === rowData.ref);
                                        return testDetail ? testDetail.title : '-';
                                    }} />

                                    <Column header={t('candidate_details_table.1')} body={(rowData) => {
                                        const testDetail = testsDetails.find(test => test.ref === rowData.ref);
                                        return testDetail ? testDetail.sub_title : '-';
                                    }} />

                                    <Column header={t('candidate_details_table.2')} body={testScoreTemplate} />
                                    <Column header={t('candidate_details_table.3')} body={testStatusTemplate} />
                                    <Column header={t('candidate_details_table.4')} body={testDateTemplate} />
                                    <Column
                                        header={t('candidate_details_table.5')}
                                        className='is-justify-content-center'
                                        body={(rowData) => {
                                            const chosenTest = candidate?.choosen_tests?.find(test => Object.keys(test)[0] === rowData.ref);
                                            const attempt_ref = chosenTest ? chosenTest[rowData.ref] : null;
                                            const isUnlocked = candidate.test_access || candidate?.unlocked_tests?.includes(attempt_ref);

                                            return (
                                                <div className={clsx('buttons are-small', { 'is-justify-content-center': true })}>
                                                    <span className="icon">
                                                        <i className={clsx('fa-solid', { 'fa-lock-open': isUnlocked, 'fa-lock': !isUnlocked })}></i>
                                                    </span>
                                                </div>
                                            );
                                        }}
                                    />

                                </DataTable>

                            </div>
                        </div>

                    </>
                )}

                {tab === 'questionnaire' && renderQuestions(campaign.questions, candidate.answers)}

            </div>
        </section>
    );
};

export default CandidateDetails;